"use client";

import { useRef, useEffect, useState } from "react";
import { SiteImage } from "@/components/site-image";

interface TeamMember {
  name: string;
  title: string;
  photo: string;
  highlights: string[];
  bio: string;
}

export function TeamCard({ member }: { member: TeamMember }) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold: 0.6 },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {/* Photo card */}
      <div
        className="group relative overflow-hidden rounded-2xl border border-border outline-none focus-visible:ring-2 focus-visible:ring-em-blue focus-visible:ring-offset-2"
        tabIndex={0}
      >
        <div className="aspect-[3/4] bg-em-blue/10">
          <SiteImage
            src={member.photo}
            alt={member.name}
            width={600}
            height={800}
            sizes="(max-width: 1024px) 100vw, 33vw"
            className="h-full w-full object-cover"
          />
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-em-navy via-em-navy/60 to-transparent" />
        <div className="absolute inset-0 bg-em-navy/90 opacity-0 transition-opacity duration-300 lg:group-hover:opacity-100 lg:group-focus:opacity-100" />

        {/* Name / title / badges overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3
            className="text-lg font-bold text-white"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {member.name}
          </h3>
          <p className="text-sm text-em-blue font-medium mb-2">
            {member.title}
          </p>

          {/* Bio — mobile only, revealed on scroll */}
          <div
            className={`lg:hidden grid transition-[grid-template-rows] duration-300 ${active ? "grid-rows-[1fr] mt-2" : "grid-rows-[0fr]"}`}
          >
            <div className="overflow-hidden">
              <p className="text-xs text-white/70 leading-relaxed">
                {member.bio}
              </p>
            </div>
          </div>

          {/* Bio — desktop, revealed over the photo */}
          <div className="hidden grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity] duration-300 lg:grid lg:group-hover:mt-2 lg:group-hover:grid-rows-[1fr] lg:group-hover:opacity-100 lg:group-focus:mt-2 lg:group-focus:grid-rows-[1fr] lg:group-focus:opacity-100">
            <div className="overflow-hidden">
              <p className="text-xs leading-relaxed text-white/80">
                {member.bio}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
